import { Combobox } from '@bambi/component-library/src/components/inputs/Combobox/Combobox';
import type { ComboboxProps } from '@bambi/component-library/src/components/inputs/Combobox/Combobox';
import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import type { InputHTMLAttributes } from 'react';
import React, { useCallback } from 'react';

import { SupportedCountryCodes } from '../BambiMapView/constants';
import { LOCATION_SEARCH_DEBOUNCE_MS } from './constants';
import { isSuggestionStreetAddress } from './isSuggestionStreetAddress';
import { searchForLocationSuggestions } from 'src/common/external/aws/searchForLocationSuggestions';
import { useCurrentOrganizationHeadquartersLocation } from 'src/features/auth/useCurrentOrganizationHeadquartersLocation';

type additionalResultsQueryFnType = (
  query: string,
  options: SelectOption[]
) => Promise<SelectOption[]>;

const defaultAdditionalResultsQueryFn: additionalResultsQueryFnType = (
  _query,
  options
) => Promise.resolve(options);

export type LocationTypeaheadProps = {
  onChange?: (value: SelectOption) => void;
  value?: SelectOption;
  placeholder?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  error?: string;
  disabled?: boolean;
  additionalResultsQueryFn?: additionalResultsQueryFnType;
  renderOption?: (
    option: SelectOption,
    selected: boolean,
    active: boolean
  ) => React.ReactNode;
};

export const LocationTypeahead = React.forwardRef(function LocationTypeahead(
  {
    onChange,
    value,
    placeholder = 'Start typing and select an address',
    inputProps,
    error,
    disabled,
    additionalResultsQueryFn = defaultAdditionalResultsQueryFn,
    renderOption,
  }: LocationTypeaheadProps,
  forwardedRef: React.Ref<HTMLInputElement>
) {
  const queryFn = useLocationTypeAheadQueryFn({
    additionalResultsQueryFn,
  });

  return (
    <Combobox
      disabled={disabled}
      dataTestId={inputProps?.id}
      keepValue
      onChange={onChange}
      options={value ? [{ value: value.value, label: value.label }] : []}
      value={value}
      placeholder={placeholder}
      ref={forwardedRef}
      queryFn={queryFn}
      queryDebounceMs={LOCATION_SEARCH_DEBOUNCE_MS}
      renderOption={renderOption}
      error={error}
    />
  );
});

type useLocationTypeAheadQueryFnProps = {
  additionalResultsQueryFn: LocationTypeaheadProps['additionalResultsQueryFn'];
};

function useLocationTypeAheadQueryFn({
  additionalResultsQueryFn = defaultAdditionalResultsQueryFn,
}: useLocationTypeAheadQueryFnProps): ComboboxProps['queryFn'] {
  const { headquartersLocation: hqLocation } =
    useCurrentOrganizationHeadquartersLocation();

  return useCallback(
    async (query: string) => {
      let mappedSuggestions: SelectOption[] = [];
      if (query) {
        const suggestions = await searchForLocationSuggestions(
          query,
          hqLocation
            ? {
                lat: hqLocation.latitude,
                long: hqLocation.longitude,
              }
            : undefined,
          SupportedCountryCodes
        );

        mappedSuggestions = suggestions
          .filter((suggestion) => {
            return (
              isSuggestionStreetAddress(suggestion.text) &&
              // We don't want to show any suggestions that don't have a placeId
              // A placeId is required by our system
              typeof suggestion.placeId === 'string'
            );
          })
          .map((suggestion) => {
            return {
              // TODO: Find a better way to narrow this type closer to the comment above
              value: suggestion.placeId as string,
              label: suggestion.text,
            };
          });
      }

      return additionalResultsQueryFn(query, mappedSuggestions);
    },
    [additionalResultsQueryFn, hqLocation]
  );
}
